// src/oura/components/onboarding/AccountReady.tsx
//
// "Your Oura account is ready" (one-off tour, #4675, backend #6473). Clients
// onboarded by hand before the onboarding flow (the retrofitted ones: Lush, Bee
// Choo, Ownlab) never got Oura's own email: the retrofit recorded their login and
// access as given before the flow and sent nothing. For them the Onboarding tab's
// Next card sends it in place of the Setup email. The button asks first, because
// it lands in the client's inbox.

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { PillButton, TYPE } from "@oura/ui";
import { sendAccountReady, sendAccountReadyTest, type Onboarding } from "@service/aeo/onboarding";
import { cn } from "../../lib/cn";
import { callName, dayMonth } from "../../onboarding/derive";

/**
 * The confirmation behind the Next card's "Send email" (#4675). With `test`, the one behind
 * "Send Internal Test": the same email to our own inbox only, changing nothing.
 */
export function AccountReadyDialog({ onboarding: o, test = false, onClose, onSent }: {
    onboarding: Onboarding;
    test?: boolean;
    onClose: () => void;
    onSent: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    // An earlier send did not finish and its outcome is unknown (backend #6475): staff who
    // checked the client's inbox may send again explicitly, and only then.
    const [unconfirmed, setUnconfirmed] = useState(false);
    const ready = o.accountReady;
    const to = ready?.to ?? null;
    const testTo = ready?.testTo ?? null;
    const name = (to && callName(to.name)) || "The client";

    const send = async (resendUnknown = false) => {
        if (busy) return;
        setBusy(true);
        setRefused(null);
        const r = await sendAccountReady(o.businessId, { resendUnknown });
        setBusy(false);
        if (!r.ok) {
            setRefused(r.message);
            setUnconfirmed(r.unconfirmed);
            return;
        }
        onSent();
    };

    const sendTest = async () => {
        if (busy) return;
        setBusy(true);
        setRefused(null);
        const r = await sendAccountReadyTest(o.businessId);
        setBusy(false);
        if (!r.ok) {
            setRefused(r.message);
            return;
        }
        onSent();
    };

    if (test) {
        return (
            <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
                <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl" data-account-ready-test-dialog="">
                    <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">
                        Send a test to {testTo ?? "our inbox"}?
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                            <p className="m-0">The same &ldquo;Your Oura account is ready&rdquo; email {name === "The client" ? "the client" : name} would get goes to {testTo ?? "our inbox"} only.</p>
                            <p className="m-0 text-gray-500">Its button is a test link that logs nobody in. Nothing changes for the client: their current link keeps working and nothing is recorded as sent.</p>
                            {refused && <p role="alert" className="m-0 font-medium text-rose-600">{refused}</p>}
                        </div>
                    </DialogDescription>
                    <div className="mt-5 flex justify-end gap-2">
                        <PillButton size="sm" disabled={busy} onClick={onClose}>Cancel</PillButton>
                        <PillButton size="sm" variant="primary" busy={busy} disabled={!testTo} onClick={() => void sendTest()} data-account-ready-send-test="">
                            {busy ? "Sending…" : "Send the test"}
                        </PillButton>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
            <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl" data-account-ready-dialog="">
                <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">
                    Send &ldquo;Your Oura account is ready&rdquo;?
                </DialogTitle>
                <DialogDescription asChild>
                    <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                        <p className="m-0">
                            {to ? (
                                <>
                                    {callName(to.name) || "The client"} gets the one-off tour email at{" "}
                                    <span className="font-medium text-gray-900">{to.email}</span>, with a link to set their password and open their Setup page.
                                </>
                            ) : (
                                "The client gets the one-off tour email, with a link to set their password and open their Setup page."
                            )}
                        </p>
                        <p className="m-0 text-gray-500">
                            {ready?.sentAt ? `Last sent ${dayMonth(ready.sentAt)}. ` : ""}A new link replaces the last one they were sent. Oura gets a blind copy.
                        </p>
                        {refused && <p role="alert" className="m-0 font-medium text-rose-600">{refused}</p>}
                    </div>
                </DialogDescription>
                <div className="mt-5 flex justify-end gap-2">
                    <PillButton size="sm" disabled={busy} onClick={onClose}>Cancel</PillButton>
                    {unconfirmed ? (
                        <PillButton size="sm" variant="primary" busy={busy} onClick={() => void send(true)} data-account-ready-send-anyway="">
                            {busy ? "Sending…" : "Send anyway"}
                        </PillButton>
                    ) : (
                        <PillButton size="sm" variant="primary" busy={busy} onClick={() => void send()} data-account-ready-send="">
                            {busy ? "Sending…" : "Send email"}
                        </PillButton>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
